/**
 * MobileMapControls - All 4 map control buttons in a horizontal row for mobile
 *
 * @atomic - Single purpose: map controls for mobile
 * @idempotent - Pure component with React.memo
 * @modular - Uses MapContext, callbacks via props
 *
 * Purpose: Replaces ALL bottom-right Leaflet controls on mobile (hidden via CSS).
 * Renders as a horizontal row attached below ImmersiveMapPanelContent, always visible
 * in every panel state — acting as a natural buffer between the panel and screen bottom.
 *
 * Desktop: hidden via md:hidden. Leaflet native controls render normally.
 *
 * Button order matches the Leaflet vertical stack (top→bottom = left→right):
 *   Spot → My Location → Zoom In → Zoom Out
 */

import { memo, useCallback, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createLogger } from '../../utils/logger'
import { trackSpotModeActivation, trackButtonClick } from '../../utils/analytics'
import { LanguageIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useLanguageChange } from '../../hooks/useLanguageChange'
import LanguageSelector from './LanguageSelector'
import MapControlHint from './MapControlHint'

const logger = createLogger('MobileMapControls')

const btnBase =
  'w-11 h-11 bg-white/90 backdrop-blur-md border border-gray-200 rounded-lg ' +
  'flex items-center justify-center text-gray-700 shadow-md ' +
  'active:scale-95 transition-transform select-none'

const spotBtnBase =
  'w-11 h-11 backdrop-blur-md border rounded-lg ' +
  'flex items-center justify-center text-white shadow-md ' +
  'active:scale-95 transition-transform select-none ' +
  'bg-[rgb(var(--color-primary))] border-[rgb(var(--color-primary))]'

// Info badge — always visible on every button; tap to show/hide context tooltip.
const InfoBadge = ({ btnKey, onToggle }) => (
  <button
    type="button"
    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-white rounded-full border border-gray-300 flex items-center justify-center z-10 shadow-sm hover:border-primary transition-colors"
    aria-label="More info"
    onClick={(e) => onToggle(btnKey, e)}
    onTouchEnd={(e) => { e.preventDefault(); onToggle(btnKey, e) }}
  >
    <InformationCircleIcon className="w-3 h-3 text-gray-400 hover:text-primary pointer-events-none" />
  </button>
)

const MobileMapControls = memo(function MobileMapControls({
  mapRef,
  onLocationClick,
  onSpotClick,
  onListingsToggle,
  isListingsActive = false,
  onPostListing,
  className = ''
}) {
  const { t, i18n } = useTranslation()
  const handleLanguageChange = useLanguageChange()
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef(null)
  const shortCode = i18n.language.split('-')[0].toUpperCase()

  // Tooltip state — one active tooltip at a time, toggled by the info badge on each button.
  // No localStorage, no tour, no auto-show: always available whenever the user wants context.
  const [activeTooltip, setActiveTooltip] = useState(null)

  const toggleTooltip = useCallback((key, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setActiveTooltip(prev => prev === key ? null : key)
  }, [])

  const dismissTooltip = useCallback(() => setActiveTooltip(null), [])

  // Close language dropdown on outside click
  useEffect(() => {
    if (!langOpen) return
    const onPointerDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [langOpen])

  const handleZoomIn = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      trackButtonClick('zoom_in', 'MobileMapControls')
      if (mapRef?.current) mapRef.current.zoomIn()
      else logger.warn('mapRef not ready on zoom in')
    },
    [mapRef]
  )

  const handleZoomOut = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      trackButtonClick('zoom_out', 'MobileMapControls')
      if (mapRef?.current) mapRef.current.zoomOut()
      else logger.warn('mapRef not ready on zoom out')
    },
    [mapRef]
  )

  const handleLocation = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      trackButtonClick('my_location', 'MobileMapControls')
      if (onLocationClick) onLocationClick()
    },
    [onLocationClick]
  )

  const handleSpot = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      trackSpotModeActivation('mobile_map_controls')
      if (onSpotClick) onSpotClick()
    },
    [onSpotClick]
  )

  const handleListingsToggle = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      const turningOn = !isListingsActive
      trackButtonClick('listings_toggle', 'MobileMapControls', { active: turningOn })
      logger.info('Listings toggle', { turningOn })
      if (onListingsToggle) onListingsToggle()
    },
    [onListingsToggle, isListingsActive]
  )

  const handlePostListing = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      trackButtonClick('post_listing', 'MobileMapControls')
      if (onPostListing) onPostListing()
    },
    [onPostListing]
  )

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Spot button */}
      {onSpotClick && (
        <div className="relative">
          <InfoBadge btnKey="spot" onToggle={toggleTooltip} />
          <button className={spotBtnBase} aria-label={t('map.spotSomething', 'Spot something')} onClick={handleSpot} onTouchEnd={handleSpot}>
            <svg className="pointer-events-none w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
          </button>
          {activeTooltip === 'spot' && (
            <MapControlHint visible onDismiss={dismissTooltip}
              title={t('mapControls.hint.spot.title', 'Spot something')}
              description={t('mapControls.hint.spot.description', 'Report a food truck, vendor or pop-up you can see right now')} />
          )}
        </div>
      )}

      {/* My Location button */}
      {onLocationClick && (
        <div className="relative">
          <InfoBadge btnKey="location" onToggle={toggleTooltip} />
          <button className={btnBase} aria-label={t('map.showMyLocation', 'Show my location')} onClick={handleLocation} onTouchEnd={handleLocation}>
            <svg className="pointer-events-none w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" />
              <line x1="12" y1="2" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="22" />
              <line x1="2" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="22" y2="12" />
            </svg>
          </button>
          {activeTooltip === 'location' && (
            <MapControlHint visible onDismiss={dismissTooltip}
              title={t('mapControls.hint.location.title', 'My location')}
              description={t('mapControls.hint.location.description', 'Center the map on where you are')} />
          )}
        </div>
      )}

      {/* Zoom In */}
      <div className="relative">
        <InfoBadge btnKey="zoomIn" onToggle={toggleTooltip} />
        <button className={btnBase} aria-label={t('map.zoomIn', 'Zoom in')} onClick={handleZoomIn} onTouchEnd={handleZoomIn}>
          <svg className="pointer-events-none w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        {activeTooltip === 'zoomIn' && (
          <MapControlHint visible onDismiss={dismissTooltip}
            title={t('mapControls.hint.zoomIn.title', 'Zoom in')}
            description={t('mapControls.hint.zoomIn.description', 'Get a closer look at the map')} />
        )}
      </div>

      {/* Zoom Out */}
      <div className="relative">
        <InfoBadge btnKey="zoomOut" onToggle={toggleTooltip} />
        <button className={btnBase} aria-label={t('map.zoomOut', 'Zoom out')} onClick={handleZoomOut} onTouchEnd={handleZoomOut}>
          <svg className="pointer-events-none w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        {activeTooltip === 'zoomOut' && (
          <MapControlHint visible onDismiss={dismissTooltip}
            title={t('mapControls.hint.zoomOut.title', 'Zoom out')}
            description={t('mapControls.hint.zoomOut.description', 'See a wider area of the map')} />
        )}
      </div>

      {/* Language picker */}
      <div ref={langRef} className="relative">
        <InfoBadge btnKey="language" onToggle={toggleTooltip} />
        <button className={btnBase} aria-label={t('map.changeLanguage', 'Change language')} aria-expanded={langOpen}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLangOpen(v => !v) }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setLangOpen(v => !v) }}>
          <LanguageIcon className="pointer-events-none w-5 h-5" />
        </button>
        {langOpen && (
          <div className="absolute right-0 bottom-full mb-2 z-[1100] w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3">
            <LanguageSelector compact onLanguageChange={(lang) => { handleLanguageChange(lang); setLangOpen(false) }} />
          </div>
        )}
        {activeTooltip === 'language' && (
          <MapControlHint visible onDismiss={dismissTooltip}
            title={t('mapControls.hint.language.title', 'Language')}
            description={t('mapControls.hint.language.description', 'Change the app language')} />
        )}
      </div>

      {/* Post listing — shown when listings layer is active */}
      {isListingsActive && onPostListing && (
        <div className="relative">
          <InfoBadge btnKey="postListing" onToggle={toggleTooltip} />
          <button className="w-11 h-11 backdrop-blur-md border rounded-lg flex items-center justify-center shadow-md active:scale-95 transition-transform select-none bg-amber-500 border-amber-500 text-white"
            aria-label={t('listings.postButton', 'Post a listing')} onClick={handlePostListing} onTouchEnd={handlePostListing}>
            <svg className="pointer-events-none w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {activeTooltip === 'postListing' && (
            <MapControlHint visible onDismiss={dismissTooltip}
              title={t('mapControls.hint.postListing.title', 'Post a listing')}
              description={t('mapControls.hint.postListing.description', 'Drop a pin and share what you have available right now — food, goods, a service, or mutual aid.')} />
          )}
        </div>
      )}

      {/* What's available — listings toggle */}
      {onListingsToggle && (
        <div className="relative">
          <InfoBadge btnKey="listings" onToggle={toggleTooltip} />
          <button
            className={isListingsActive ? 'w-11 h-11 backdrop-blur-md border rounded-lg flex items-center justify-center shadow-md active:scale-95 transition-transform select-none bg-amber-500 border-amber-500 text-white' : btnBase}
            aria-label={t('listings.toggleButton', "What's nearby")}
            onClick={handleListingsToggle} onTouchEnd={handleListingsToggle}>
            <svg className="pointer-events-none w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
          </button>
          {activeTooltip === 'listings' && (
            <MapControlHint visible onDismiss={dismissTooltip}
              title={t('mapControls.hint.listings.title', "What's available nearby")}
              description={t('mapControls.hint.listings.description', 'Browse food, goods, services and mutual aid from people near you. Sign in to post your own listing.')} />
          )}
        </div>
      )}
    </div>
  )
})


export default MobileMapControls
